import type { FC } from 'react';
import { Box } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { empresaConvenioId, estadoId } from 'src/utils/data/downloadString';
import { onGet } from '../../../store/actions/global';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import FunnelFilter from './FunnelFilter';
import FunnelProspeccion from './FunnelProspeccion';
import FunnelBacklog from './FunnelBacklog';
import FunnelGestion from './FunnelGestion';
import FunnelResumen from './FunnelResumen';

const Funnel: FC<any> = ({ onGet: onGetExec }) => {
  const tokenString = useSelector((state:{ auth:{ tokenString: string } }) => state.auth.tokenString);
  const isMountedRef = useIsMountedRef();
  const [formFilter, setFormFilter] = useState({
    startDate: new Date(),
    endDate: new Date(),
    convenio: 'all',
    channel: 'all',
    user: 'all',
    assistantManager: 'all',
    businessManager: 'all',
  });
  const [data, setData] = useState({ prospeccion: [], remesa: [], backlog: [], gestion: [], procesamiento: [], resumen: [] });
  const [assistantManagerList, setAssistantManagerList] = useState([]);
  const [businessManagerList, setBusinessManagerList] = useState([]);
  const [selectedBusinessManagerList, setSelectedBusinessManagerList] = useState([]);
  const [spinner, setSpinner] = useState({ prospeccion: false, backlog: false, gestion: false, procesamiento: false, resumen: false });
  const [dataFilter] = useState({
    convenio: [{ id: 1, name: 'Convenio 1' }],
    channel: [{ id: 1, name: 'Channel 1' }],
    user: [{ id: 1, name: 'User 1' }],
  });

  const changeFilter = (name, value) => {
    setFormFilter((values) => ({
      ...values,
      [name]: value,
    }));

    if (name === 'assistantManager') {
      const selectedList = businessManagerList.filter((bm) => bm.assistantManager === value);
      setSelectedBusinessManagerList(selectedList);
    }
  };

  const getDateString = (dateParam: Date): string => {
    const month = dateParam.getMonth() + 1;
    const day = dateParam.getDate();
    const monthString = month > 9 ? `${month}` : `0${month}`;
    const dayString = day > 9 ? `${day}` : `0${day}`;
    return `${dateParam.getFullYear()}-${monthString}-${dayString}`;
  };

  const onSubmitProspeccion = async (body: any) => {
    setSpinner((values) => ({ ...values, prospeccion: true }));
    const { prospeccion, remesa } = await onGetExec({
      url: '/dashboard/funnel/prospeccion-remesa',
      body,
      global: false,
    });
    setData((values) => ({ ...values, prospeccion, remesa }));
    setSpinner((values) => ({ ...values, prospeccion: false }));
  };

  const onSubmitBacklog = async (body: any) => {
    setSpinner((values) => ({ ...values, backlog: true }));
    const { backlog } = await onGetExec({
      url: '/dashboard/funnel/backlog',
      body,
      global: false,
    });
    setData((values) => ({ ...values, backlog }));
    setSpinner((values) => ({ ...values, backlog: false }));
  };

  const onSubmitGestion = async (body: any) => {
    setSpinner((values) => ({ ...values, gestion: true }));
    const { gestion } = await onGetExec({
      url: '/dashboard/funnel/gestion',
      body,
      global: false,
    });
    setData((values) => ({ ...values, gestion }));
    setSpinner((values) => ({ ...values, gestion: false }));
  };

  const onSubmitProcesamiento = async (body: any) => {
    setSpinner((values) => ({ ...values, procesamiento: true }));
    const { procesamiento } = await onGetExec({
      url: '/dashboard/funnel/procesamiento',
      body,
      global: false,
    });
    setData((values) => ({ ...values, procesamiento }));
    setSpinner((values) => ({ ...values, procesamiento: false }));
  };

  const onSubmitResumen = async (body: any) => {
    setSpinner((values) => ({ ...values, resumen: true }));
    const { resumen } = await onGetExec({
      url: '/dashboard/funnel/resumen',
      body,
      global: false,
    });
    setData((values) => ({ ...values, resumen }));
    setSpinner((values) => ({ ...values, resumen: false }));
  };

  const token = tokenString ?? localStorage.getItem('token');

  const onSubmitDownload = async (body: any) => {
    window.open(`${process.env.REACT_APP_API_REPORT_URL ?? ''}/fuvexadmin-dashboard/list-request-fuvex-admin-excel?empresaConvenioId=${empresaConvenioId}&estadoId=${estadoId}&fechaFin=${body.endDate}&fechaInicio=${body.startDate}&code=${token}`, '_blank');
  };

  const onSubmit = async () => {
    if (isMountedRef.current) {
      const startDate = getDateString(formFilter.startDate);
      const endDate = getDateString(formFilter.endDate);

      const payload = {
        ...formFilter,
        startDate,
        endDate,
        assistantManager: formFilter.assistantManager !== 'all'
          ? assistantManagerList.find((am) => am.code === formFilter.assistantManager)?.name
          : undefined,
        businessManager: formFilter.businessManager !== 'all'
          ? businessManagerList.find((bm) => bm.code === formFilter.businessManager)?.name
          : undefined,
      };

      onSubmitBacklog(payload);
      onSubmitProspeccion(payload);
      onSubmitProcesamiento(payload);
      onSubmitResumen(payload);
      onSubmitGestion(payload);
    }
  };

  const onDownload = async () => {
    if (isMountedRef.current) {
      const startDate = getDateString(formFilter.startDate);
      const endDate = getDateString(formFilter.endDate);
      const body = { ...formFilter, startDate, endDate };
      onSubmitDownload(body);
    }
  };

  const getAssistantAndBusinessManagerList = async () => {
    const response = await onGetExec({
      url: '/dashboard/assistantManager',
    });

    setAssistantManagerList(response.assistantManagerList);
    setBusinessManagerList(response.businessManagerList);
  };

  useEffect(() => {
    getAssistantAndBusinessManagerList();
  }, []);

  return (
    <Box m={2}>
      <FunnelFilter
        onSubmit={onSubmit}
        onDownload={onDownload}
        formFilter={formFilter}
        assistantManagerList={assistantManagerList}
        businessManagerList={selectedBusinessManagerList}
        changeFilter={changeFilter}
        data={dataFilter}
      />
      <FunnelProspeccion prospeccion={data.prospeccion} remesa={data.remesa} spinner={spinner.prospeccion} />
      <FunnelBacklog data={data.backlog} spinner={spinner.backlog} />
      <FunnelGestion gestion={data.gestion} procesamiento={data.procesamiento} spinnerGestion={spinner.gestion} spinnerProcesamiento={spinner.procesamiento} />
      <FunnelResumen data={data.resumen} spinner={spinner.resumen} />
    </Box>
  );
};

export default connect(null, {
  onGet,
})(Funnel);
